import { useEffect, useState } from "react";
import api, { downloadBlob } from "../lib/api";
import EmptyState from "./EmptyState";
import { Search, Plus, Pencil, Boxes, Download, Power, Trash2, RotateCcw } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function CatalogManager({ canEdit }) {
  const { t } = useTranslation();
  const [all, setAll] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  // excludeTarget = { supply, preselectedBranchId }
  const [excludeTarget, setExcludeTarget] = useState(null);
  const [excludeSelectedBranch, setExcludeSelectedBranch] = useState("");

  const load = () => {
    api.get("/supplies", { params: { active_only: false } }).then((r) => setAll(r.data));
    api.get("/supplies/categories").then((r) => setCategories(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    if (canEdit) api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const branchName = (id) => {
    const b = branches.find((x) => x.id === id);
    return b ? (b.code ? `${b.code} — ${b.name}` : b.name) : id;
  };

  const openExcludeModal = (supply) => {
    setExcludeTarget(supply);
    // Pre-select the branch filter if one is active and not already excluded
    const preselect = branchFilter && !(supply.excluded_branches || []).includes(branchFilter)
      ? branchFilter
      : "";
    setExcludeSelectedBranch(preselect);
  };

  const doExclude = async () => {
    if (!excludeTarget || !excludeSelectedBranch) return;
    try {
      await api.delete(`/supplies/${excludeTarget.id}/branches/${excludeSelectedBranch}`);
      toast.success(t("catalog.supplyExcluded"));
      setExcludeTarget(null);
      setExcludeSelectedBranch("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const doRestoreToBranch = async (supply, branchId) => {
    try {
      await api.post(`/supplies/${supply.id}/branches/${branchId}/restore`);
      toast.success(t("catalog.supplyRestoredToBranch"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const activeCount = all.filter((s) => s.active !== false).length;
  const inactiveCount = all.length - activeCount;

  const filtered = all.filter((s) => {
    if (!showInactive && s.active === false) return false;
    if (category && s.category !== category) return false;
    if (search && !s.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const toggleActive = async (s) => {
    try {
      if (s.active === false) {
        await api.post(`/supplies/${s.id}/restore`);
        toast.success(t("catalog.supplyRestored"));
      } else {
        if (!window.confirm(t("catalog.deactivateConfirm"))) return;
        await api.delete(`/supplies/${s.id}`);
        toast.success(t("catalog.supplyDeactivated"));
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const r = await api.get("/supplies/export", { responseType: "blob" });
      downloadBlob(r.data, "catalogo_insumos.xlsx");
    } catch {
      toast.error(t("common.error"));
    } finally {
      setExporting(false);
    }
  };

  const createdByLabel = (s) => {
    if (s.created_by_role === "supplier") return t("catalog.createdBySupplier");
    if (s.created_by_bulk_load) return t("catalog.createdByBulk");
    return t("catalog.createdByCorp");
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-sm text-slate-500">
          <span className="font-bold text-[#1A1A1A]">{activeCount}</span> {t("catalog.activeSuppliesLabel")}
          {" · "}<span className="font-bold text-slate-600">{inactiveCount}</span> {t("catalog.inactiveSuppliesLabel")}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input data-testid="catalog-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={t("catalog.searchPlaceholder")}
              className="pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] w-52" />
          </div>
          <select data-testid="catalog-category-filter" value={category} onChange={(e) => setCategory(e.target.value)}
            className="px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]">
            <option value="">{t("catalog.allCategories")}</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {canEdit && (
            <select data-testid="catalog-branch-filter" value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}
              className="px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]">
              <option value="">{t("catalog.branchFilterAll")}</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.code ? `${b.code} — ${b.name}` : b.name}</option>)}
            </select>
          )}
          <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer px-2">
            <input data-testid="catalog-show-inactive" type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            {t("catalog.showInactive")}
          </label>
          <button data-testid="catalog-export-btn" onClick={exportXlsx} disabled={exporting}
            className="px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-50">
            <Download className="w-4 h-4" /> {t("catalog.exportExcel")}
          </button>
          {canEdit && (
            <button data-testid="catalog-new-btn" onClick={() => setEditing("new")}
              className="px-4 py-2.5 bg-[#1A1A1A] text-white rounded-xl text-sm font-bold flex items-center gap-2 hover:bg-black">
              <Plus className="w-4 h-4" /> {t("catalog.newSupply")}
            </button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title={t("catalog.emptyTitle")} message={t("catalog.emptyMessage")} icon={Boxes} />
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("catalog.name")}</th>
                <th className="text-left px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("catalog.category")}</th>
                <th className="text-left px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("catalog.unit")}</th>
                <th className="text-left px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("catalog.posSku")}</th>
                <th className="text-left px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("catalog.createdBy")}</th>
                <th className="text-left px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("catalog.statusCol")}</th>
                {canEdit && <th className="text-left px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("catalog.excludedBranches")}</th>}
                {canEdit && <th className="px-5 py-3"></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} data-testid={`catalog-row-${s.id}`}
                  className={`border-b border-slate-100 last:border-0 hover:bg-slate-50 ${s.active === false ? "opacity-60" : ""}`}>
                  <td className="px-5 py-3 text-sm font-semibold text-[#1A1A1A]">{s.name}{s.presentation && <div className="text-[11px] text-slate-400 font-normal">{s.presentation}</div>}</td>
                  <td className="px-5 py-3"><span className="inline-block px-2 py-0.5 bg-slate-100 text-slate-700 text-[11px] font-semibold rounded capitalize">{s.category}</span></td>
                  <td className="px-5 py-3 text-sm text-slate-600 capitalize">{s.unit}</td>
                  <td className="px-5 py-3 font-mono text-xs text-slate-500">{s.pos_sku || "—"}</td>
                  <td className="px-5 py-3 text-xs text-slate-500">{createdByLabel(s)}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-block px-2 py-0.5 text-[11px] font-bold rounded ${s.active === false ? "bg-slate-200 text-slate-600" : "bg-emerald-100 text-emerald-700"}`}>
                      {s.active === false ? t("catalog.inactive") : t("catalog.active")}
                    </span>
                  </td>
                  {canEdit && (
                    <td className="px-5 py-3" data-testid={`catalog-excluded-${s.id}`}>
                      {(s.excluded_branches || []).length === 0 ? (
                        <span className="text-xs text-slate-400">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(s.excluded_branches || []).map((bid) => (
                            <span key={bid} className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 text-[10px] font-semibold rounded border border-red-200">
                              {branchName(bid)}
                              <button data-testid={`catalog-restore-branch-${s.id}-${bid}`} onClick={() => doRestoreToBranch(s, bid)}
                                title={t("catalog.restoreToBranch")} className="hover:text-red-900">
                                <RotateCcw className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  )}
                  {canEdit && (
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* Quitar de sucursal: visible para todos los insumos activos cuando hay al menos una sucursal no excluida */}
                        {s.active !== false && branches.some((b) => !(s.excluded_branches || []).includes(b.id)) && (
                          <button data-testid={`catalog-exclude-${s.id}`} onClick={() => openExcludeModal(s)}
                            title={t("catalog.excludeFromBranch")}
                            className="w-8 h-8 rounded-lg hover:bg-red-50 text-red-500 flex items-center justify-center">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                        <button data-testid={`catalog-edit-${s.id}`} onClick={() => setEditing(s)}
                          className="w-8 h-8 rounded-lg hover:bg-slate-100 text-slate-600 flex items-center justify-center">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button data-testid={`catalog-toggle-${s.id}`} onClick={() => toggleActive(s)}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center ${s.active === false ? "hover:bg-emerald-50 text-emerald-600" : "hover:bg-red-50 text-red-500"}`}>
                          <Power className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <SupplyFormModal supply={editing === "new" ? null : editing} categories={categories}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}

      {excludeTarget && (
        <AnimatePresence>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} data-testid="catalog-exclude-modal"
              className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200">
                <h2 className="font-display font-bold text-base">{t("catalog.excludeConfirmTitle")}</h2>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-sm text-slate-700 font-semibold">"{excludeTarget.name}"</p>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1.5">
                    {t("catalog.selectBranchToExclude")}
                  </label>
                  <select
                    data-testid="catalog-exclude-branch-select"
                    value={excludeSelectedBranch}
                    onChange={(e) => setExcludeSelectedBranch(e.target.value)}
                    className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                  >
                    <option value="">{t("catalog.branchFilterAll")}</option>
                    {branches
                      .filter((b) => !(excludeTarget.excluded_branches || []).includes(b.id))
                      .map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.code ? `${b.code} — ${b.name}` : b.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
                  {t("catalog.excludeStockWarning")}
                </div>
              </div>
              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
                <button onClick={() => { setExcludeTarget(null); setExcludeSelectedBranch(""); }}
                  className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-100">
                  {t("common.cancel")}
                </button>
                <button
                  data-testid="catalog-exclude-confirm-btn"
                  onClick={doExclude}
                  disabled={!excludeSelectedBranch}
                  className="px-5 py-2 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed">
                  {t("catalog.excludeFromBranch")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}

function SupplyFormModal({ supply, categories, onClose, onSaved }) {
  const { t } = useTranslation();
  const isEdit = !!supply;
  const [form, setForm] = useState({
    name: supply?.name || "",
    category: supply?.category || "",
    unit: supply?.unit || "",
    presentation: supply?.presentation || "",
    pos_sku: supply?.pos_sku || "",
    active: supply ? supply.active !== false : true,
  });
  const [submitting, setSubmitting] = useState(false);

  const save = async () => {
    if (form.name.trim().length < 2) { toast.error(t("catalog.nameRequired")); return; }
    if (!form.category.trim()) { toast.error(t("catalog.categoryRequired")); return; }
    if (!form.unit.trim()) { toast.error(t("catalog.unitRequired")); return; }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category.trim(),
        unit: form.unit.trim(),
        presentation: form.presentation.trim() || undefined,
        pos_sku: form.pos_sku.trim() || undefined,
      };
      if (isEdit) {
        await api.put(`/supplies/${supply.id}`, { ...payload, active: form.active });
        toast.success(t("catalog.supplyUpdated"));
      } else {
        await api.post("/supplies", payload);
        toast.success(t("catalog.supplyCreated"));
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} data-testid="supply-form-modal"
          className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="font-display font-bold text-base">{isEdit ? t("catalog.editSupply") : t("catalog.newSupply")}</h2>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">{t("catalog.name")}</label>
              <input data-testid="supply-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="inp" autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">{t("catalog.category")}</label>
                <input data-testid="supply-category-input" list="catalog-cats" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="inp" />
                <datalist id="catalog-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
              </div>
              <div>
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">{t("catalog.unit")}</label>
                <input data-testid="supply-unit-input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="inp" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">{t("catalog.presentation")}</label>
                <input data-testid="supply-presentation-input" value={form.presentation} onChange={(e) => setForm({ ...form, presentation: e.target.value })} className="inp" />
              </div>
              <div>
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">{t("catalog.posSku")}</label>
                <input data-testid="supply-sku-input" value={form.pos_sku} onChange={(e) => setForm({ ...form, pos_sku: e.target.value })} className="inp" />
              </div>
            </div>
            {isEdit && (
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
                <input data-testid="supply-active-toggle" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                {t("catalog.active")}
              </label>
            )}
          </div>
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-100">{t("common.cancel")}</button>
            <button data-testid="supply-save-btn" onClick={save} disabled={submitting} className="btn-primary-chizy px-5 py-2 rounded-xl text-sm disabled:opacity-50">
              {submitting ? t("common.saving") : t("common.save")}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
